import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, ArrowRight, Server, KeyRound, ArrowLeft, ShieldCheck } from 'lucide-react';

export default function PremiumAuth() {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResettingNewPassword, setIsResettingNewPassword] = useState(false);

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPasswordData, setNewPasswordData] = useState({ newPassword: '', confirmPassword: '' });
  const [resetSession, setResetSession] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const res = await axios.post(`${endpoint}`, formData);
      
      if (res.data.success) {
        // If temporary password was used, prompt user to set a permanent password
        if (res.data.mustResetPassword) {
          toast.info('Temporary password detected. Please set your new permanent password.');
          setResetSession({ token: res.data.token, user: res.data.user });
          setIsResettingNewPassword(true);
          return;
        }

        toast.success(isLogin ? 'Logged in successfully!' : 'Account created successfully! Check your email for confirmation.');
        login(res.data.user, res.data.token);
        navigate('/clients');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Authentication failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setIsSendingReset(true);
    try {
      const res = await axios.post('/api/auth/forgot-password', { email: forgotEmail });
      if (res.data.success) {
        toast.success(res.data.message || 'Temporary password sent to your email!');
        setFormData(prev => ({ ...prev, email: forgotEmail, password: '' }));
        setIsForgotPassword(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send temporary password');
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleSaveNewPassword = async (e) => {
    e.preventDefault();
    if (!newPasswordData.newPassword || !newPasswordData.confirmPassword) {
      toast.error('Please enter and confirm your new password');
      return;
    }

    if (newPasswordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsSavingPassword(true);
    try {
      const res = await axios.post(
        '/api/auth/reset-password',
        { newPassword: newPasswordData.newPassword },
        { headers: { Authorization: `Bearer ${resetSession?.token}` } }
      );

      if (res.data.success) {
        toast.success('Password updated successfully! Welcome to TaxEases.');
        login(resetSession.user, resetSession.token);
        navigate('/clients');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update password');
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <ToastContainer />
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-md bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-700"
      >
        <div className="p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30">
              {isResettingNewPassword ? (
                <ShieldCheck className="w-8 h-8 text-white" />
              ) : isForgotPassword ? (
                <KeyRound className="w-8 h-8 text-white" />
              ) : (
                <Server className="w-8 h-8 text-white" />
              )}
            </div>
          </div>

          {isResettingNewPassword ? (
            /* Set Permanent Password View */
            <div>
              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Set New Password
              </h2>
              <p className="text-gray-400 text-center text-sm mb-6">
                You logged in with a temporary password. Please create your permanent password to continue.
              </p>

              <form onSubmit={handleSaveNewPassword} className="space-y-5">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    name="newPassword"
                    placeholder="New Password (min. 6 characters)"
                    value={newPasswordData.newPassword}
                    onChange={(e) => setNewPasswordData({ ...newPasswordData, newPassword: e.target.value })}
                    required
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl py-3 pl-12 pr-4 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm New Password"
                    value={newPasswordData.confirmPassword}
                    onChange={(e) => setNewPasswordData({ ...newPasswordData, confirmPassword: e.target.value })}
                    required
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl py-3 pl-12 pr-4 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSavingPassword}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSavingPassword ? 'Saving Password...' : 'Save Password & Continue'}
                  {!isSavingPassword && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>
            </div>
          ) : isForgotPassword ? (
            /* Forgot Password Flow */
            <div>
              <h2 className="text-3xl font-bold text-white text-center mb-2">
                Forgot Password
              </h2>
              <p className="text-gray-400 text-center text-sm mb-8">
                Enter your registered email and we will send a temporary password directly to your inbox.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-6">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    name="forgotEmail"
                    placeholder="Registered Email Address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl py-3 pl-12 pr-4 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSendingReset}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSendingReset ? 'Sending Temporary Password...' : 'Send Password to Email'}
                  {!isSendingReset && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(false)}
                  className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            /* Sign In / Sign Up Flow */
            <div>
              <h2 className="text-3xl font-bold text-white text-center mb-2">
                Premium Access
              </h2>
              <p className="text-gray-400 text-center mb-8">
                {isLogin ? 'Sign in to sync your tax data' : 'Create an account to backup your returns'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl py-3 pl-12 pr-4 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    name="password"
                    placeholder="Password / Temporary Code"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl py-3 pl-12 pr-4 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  />
                </div>

                {isLogin && (
                  <div className="flex justify-end -mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(formData.email || '');
                        setIsForgotPassword(true);
                      }}
                      className="text-xs text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
                >
                  {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
